/**
 * Agent Routed Exceptions Management Page
 * 
 * Displays contracts with low-confidence AI classifications requiring human review
 * Provides interface to browse, filter, sort, and access exception details
 * 
 * Features:
 * - Paginated table view of contracts with exceptions
 * - Search by contract name or ID
 * - Status filtering (Exceptions Present / No Exceptions)
 * - Multi-column sorting (name, confidence, count, date)
 * - Confidence color coding (red <40%, orange 40-60%, default >60%)
 * - Exception badge display showing product/channel/territory counts
 * - Responsive pagination controls with previous/next navigation
 * - Click-to-detail navigation to contract review page
 * 
 * Data Flow:
 * 1. Fetch taxonomy data on mount (product/channel/territory categories)
 * 2. Fetch contracts with exceptions from API
 * 3. Apply filters (search, status) and sorting
 * 4. Paginate results (20 rows per page)
 * 5. Display in interactive table with click navigation
 * 
 * API Integration:
 * - GET /taxonomy/: Fetch taxonomy categories (levels 1-3)
 * - GET /agent/exceptions/: Fetch contracts with low-confidence exceptions
 * 
 * Dependencies:
 * - lucide-react: Icons (ChevronLeft, ChevronRight)
 * - Bootstrap 5: Table, form controls, badges, alerts
 * - config_mapping: API response codes
 * - server_utils: API communication (apiCall)
 * - config_ip_details: API server URL
 * 
 * @component
 * @returns {JSX.Element} Exceptions management page with filterable contract table
 * 
 * @example
 * // Usage in router
 * <Route path="/agent-routed-exceptions" element={<AgentRoutedExceptions />} />
 */

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { API_SUCCESS_RESPONSE_CODE } from "../../utils/config_mapping";
import { apiCall } from "../../utils/server_utils";
import { API_SERVER_URL } from "../../utils/config_ip_details";
import CommonIcon from '../common/customicon';
import HeaderLogo  from "../structure/headerlogo";

/**
 * TypeScript Type Definitions (Reference Only)
 * 
 * @typedef {Object} ContractException
 * @property {string} id - Unique contract identifier
 * @property {string} name - Contract name/title
 * @property {string} licensorName - Name of licensor/counterparty
 * @property {string} contractType - Type of contract (Software License, Service Agreement, etc.)
 * @property {string} status - Contract status (EXCEPTIONS_PRESENT, IN_REVIEW, NO_EXCEPTIONS)
 * @property {number} productExceptions - Count of product category exceptions
 * @property {number} channelExceptions - Count of sales channel exceptions
 * @property {number} territoryExceptions - Count of territory exceptions
 * @property {number} minConfidence - Minimum confidence score for all exceptions (0.0-1.0)
 * @property {number} totalExceptions - Total count of all exceptions
 * @property {Date} lastActivity - Timestamp of last review/update
 */

/**
 * Mock Contract Data
 * Sample contracts for development/testing
 * In production, data comes from API via fetchContractsWithExceptions()
 * 
 * @constant {ContractException[]} mockContracts
 */
const mockContracts = [
  {
    id: "CTR-2024-001",
    name: "Contract Name 1",
    licensorName: "Licensor Name 1",
    contractType: "Software License",
    status: "EXCEPTIONS_PRESENT",
    productExceptions: 3,
    channelExceptions: 1,
    territoryExceptions: 0,
    minConfidence: 0.42,
    totalExceptions: 4,
    lastActivity: new Date("2024-01-15"),
  },
  {
    id: "CTR-2024-002",
    name: "Contract Name 2",
    licensorName: "Licensor Name 2",
    contractType: "Service Agreement",
    status: "IN_REVIEW",
    productExceptions: 0,
    channelExceptions: 2,
    territoryExceptions: 1,
    minConfidence: 0.38,
    totalExceptions: 3,
    lastActivity: new Date("2024-01-14"),
  },
  {
    id: "CTR-2024-003",
    name: "Contract Name 3",
    licensorName: "Licensor Name 3",
    contractType: "Professional Services",
    status: "EXCEPTIONS_PRESENT",
    productExceptions: 2,
    channelExceptions: 0,
    territoryExceptions: 2,
    minConfidence: 0.51,
    totalExceptions: 4,
    lastActivity: new Date("2024-01-13"),
  },
];

export default function AgentRoutedExceptions() {
  // ===== ROUTER HOOKS =====
  const navigate = useNavigate(); // For navigation to contract review page

  // ===== STATE: Search & Filters =====
  const [searchTerm, setSearchTerm] = useState(""); // Search text for contract name/ID
  const [statusFilter, setStatusFilter] = useState("all"); // Status filter value
  const [selectedProducts, setSelectedProducts] = useState([]); // Selected product categories (multi-select)
  const [selectedChannels, setSelectedChannels] = useState([]); // Selected sales channels (multi-select)
  const [selectedTerritories, setSelectedTerritories] = useState([]); // Selected territories (multi-select)

  // ===== STATE: Data & Loading =====
  const [contracts, setContracts] = useState([]); // All contracts from API
  const [loading, setLoading] = useState(true); // API data loading state
  
  // ===== STATE: Taxonomy Data =====
  // Stores level 1 categories from API for multi-select filters
  const [productTaxonomies, setProductTaxonomies] = useState([]); // Product category L1 items
  const [salesTaxonomies, setSalesTaxonomies] = useState([]); // Sales channel L1 items
  const [territoryTaxonomies, setTerritoryTaxonomies] = useState([]); // Territory L1 items

  // ===== STATE: Sorting & Pagination =====
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' }); // Current sort column and direction
  const [currentPage, setCurrentPage] = useState(1); // Current page number (1-indexed)
  const rowsPerPage = 20; // Results per page

  // ===== CONSTANTS =====
  const confidenceThreshold = 0.65; // Threshold for flagging as exception

  /**
   * Initialize Component - Fetch Taxonomy & Contracts Data
   * 
   * Called on mount to load initial data
   * Runs two parallel API calls: taxonomy and contracts
   * 
   * @effect
   * @dependency {} - Runs once on component mount
   */
  // Fetch contracts on mount
  useEffect(() => {
    fetchTaxonomyData();
    fetchContractsWithExceptions();
  }, []);

  /**
   * Fetch Taxonomy Categories from API
   * 
   * Retrieves all taxonomy categories (Product, Sales Channel, Territory)
   * Filters to level 1 categories only for the multi-select filters
   * 
   * API Endpoint: GET /taxonomy/
   * 
   * Process:
   * 1. Call API to fetch taxonomy structure
   * 2. Filter each category type to L1 items only
   * 3. Extract id and label for dropdown display
   * 4. Store in appropriate state arrays
   * 5. Handle errors gracefully with empty fallback
   * 
   * Data Structure:
   * - product_taxonomies: Product category hierarchy
   * - sales_taxonomies: Sales channel hierarchy
   * - territories_taxonomies: Territory hierarchy
   * 
   * @async
   * @function fetchTaxonomyData
   * @returns {Promise<void>}
   */
  const fetchTaxonomyData = async () => {
    try {
      const response = await apiCall("GET", API_SERVER_URL + "/taxonomy/");
      
      console.log("Taxonomy Response:", response);
      
      if (response.code === API_SUCCESS_RESPONSE_CODE && response.data) {
        // Extract level 1 categories for Product
        const productL1 = response.data.product_taxonomies
          .filter(item => item.level === 1)
          .map(item => ({ id: item.id, label: item.label }));
        setProductTaxonomies(productL1);
        
        // Extract level 1 categories for Sales Channel
        const salesL1 = response.data.sales_taxonomies
          .filter(item => item.level === 1)
          .map(item => ({ id: item.id, label: item.label }));
        setSalesTaxonomies(salesL1);
        
        // Extract level 1 categories for Territory
        const territoryL1 = response.data.territories_taxonomies
          .filter(item => item.level === 1)
          .map(item => ({ id: item.id, label: item.label }));
        setTerritoryTaxonomies(territoryL1);
      }
    } catch (error) {
      console.error("Error fetching taxonomy data:", error);
      // Use empty arrays as fallback
      setProductTaxonomies([]);
      setSalesTaxonomies([]);
      setTerritoryTaxonomies([]);
    }
  };

  /**
   * Fetch Contracts with Exceptions from API
   * 
   * Retrieves all contracts that have low-confidence AI classifications
   * Transforms API data to include JavaScript Date objects for lastActivity
   * 
   * API Endpoint: GET /agent/exceptions/
   * 
   * Process:
   * 1. Set loading state to true
   * 2. Call API to fetch exceptions
   * 3. Transform lastActivity strings to Date objects
   * 4. Store transformed data in contracts state
   * 5. Handle errors and show alert
   * 
   * @async
   * @function fetchContractsWithExceptions
   * @returns {Promise<void>}
   */
  const fetchContractsWithExceptions = async () => {
    try {
      setLoading(true);
      const response = await apiCall("GET", API_SERVER_URL + "/agent/exceptions/");
      
      console.log("Agent Routed Exceptions Response:", response);
      
      if (response.code === API_SUCCESS_RESPONSE_CODE && response.data) {
        // Transform API data to include Date objects for lastActivity
        const transformedContracts = response.data.map(contract => ({
          ...contract,
          lastActivity: contract.lastActivity ? new Date(contract.lastActivity) : new Date()
        }));
        setContracts(transformedContracts);
      }
    } catch (error) {
      console.error("Error fetching contracts with exceptions:", error);
      alert("Error loading contracts: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Filter and Sort Contracts
   * 
   * IIFE (Immediately Invoked Function Expression) that:
   * 1. Filters contracts by search term and status
   * 2. Sorts results by selected column and direction
   * 3. Returns filtered/sorted array for pagination
   * 
   * Filtering:
   * - Search: Match against contract name or ID (case-insensitive)
   * - Status: Filter by EXCEPTIONS_PRESENT, IN_REVIEW, NO_EXCEPTIONS, or ALL
   * 
   * Sorting:
   * - name: String comparison (case-insensitive)
   * - minConfidence: Numeric comparison
   * - totalExceptions: Numeric comparison
   * - lastActivity: Date comparison (via getTime())
   * 
   * @constant {ContractException[]} filteredAndSortedContracts
   */
  // Filter and sort contracts
  const filteredAndSortedContracts = (() => {
    // First, filter
    let filtered = contracts.filter((contract) => {
      const matchesSearch =
        searchTerm === "" ||
        contract.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contract.id.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === "all" ||
        contract.status === statusFilter;

      return matchesSearch && matchesStatus;
    });

    // Then, sort
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle date sorting - convert to timestamps for comparison
        if (sortConfig.key === 'lastActivity') {
          aValue = a.lastActivity.getTime();
          bValue = b.lastActivity.getTime();
        }

        // Handle numeric sorting - ensure values are numbers
        if (sortConfig.key === 'minConfidence' || sortConfig.key === 'totalExceptions') {
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
        }

        // Handle string sorting - convert to lowercase for case-insensitive comparison
        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        // Perform comparison
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  })();

  /**
   * Calculate Pagination Values
   * 
   * Determines which contracts to display on current page
   * Calculates total pages and slice indices for pagination
   * 
   * @constant {number} totalPages - Total number of pages
   * @constant {ContractException[]} paginatedContracts - Contracts for current page
   */
  // Pagination
  const totalPages = Math.ceil(filteredAndSortedContracts.length / rowsPerPage);
  const paginatedContracts = filteredAndSortedContracts.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  /**
   * Handle Row Click - Navigate to Contract Review
   * 
   * Navigates user to the contract details/review page
   * URL: /contract/{contractId}/review
   * 
   * @function handleRowClick
   * @param {ContractException} contract - Contract being clicked
   */
  const handleRowClick = (contract) => {
    navigate(`/contract/${contract.id}/review`);
  };

  /**
   * Format Date for Display
   * 
   * Converts Date object to human-readable relative format
   * Returns "Today", "Yesterday", "N days ago", or formatted date string
   * 
   * Algorithm:
   * 1. Calculate difference between now and given date
   * 2. Convert milliseconds to days
   * 3. Return appropriate label based on difference
   * 
   * @function formatDate
   * @param {Date} date - Date to format
   * @returns {string} Human-readable date string (e.g., "Today", "3 days ago", "1/15/2024")
   */
  const formatDate = (date) => {
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  /**
   * Handle Column Sort
   * 
   * Toggles sort direction when same column clicked again
   * Resets direction to ASC when different column clicked
   * 
   * Algorithm:
   * 1. Check if clicked column matches current sort column
   * 2. If same: toggle direction (ASC ↔ DESC)
   * 3. If different: set direction to ASC
   * 4. Update sortConfig state
   * 
   * @function handleSort
   * @param {string} key - Column key to sort by (name, minConfidence, totalExceptions, lastActivity)
   */
  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  /**
   * Sort Icon Component
   * 
   * Displays visual indicator of current sort state for column headers
   * Shows faded ▲▼ for non-sorted columns, filled ▲ or ▼ for sorted column
   * 
   * @component
   * @param {Object} props - Component props
   * @param {string} props.columnKey - Column key being checked
   * @returns {JSX.Element} Sort indicator icon
   */
  // Sort icon component
  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return (
        <span style={{ opacity: 0.3, marginLeft: '4px' }}>
          ▲▼
        </span>
      );
    }
    
    return (
      <span style={{ marginLeft: '4px' }}>
        {sortConfig.direction === 'asc' ? '▲' : '▼'}
      </span>
    );
  };

  /**
   * MultiSelectFilter Component
   * 
   * Reusable multi-select dropdown for filtering by categories
   * Displays selected count in badge on button
   * Closes when clicking outside (click-away listener)
   * 
   * Features:
   * - Toggle dropdown open/close
   * - Checkbox list of filterable options
   * - Selection badge showing count
   * - Outside click closes dropdown
   * - Empty state handling
   * - Flexible for products, channels, or territories
   * 
   * @component
   * @param {Object} props - Component props
   * @param {string} props.label - Filter label (e.g., "Product Categories")
   * @param {Array} props.options - Array of {id, label} objects or strings
   * @param {string[]} props.selected - Array of selected option IDs
   * @param {Function} props.onSelectedChange - Callback when selection changes
   * @returns {JSX.Element} Dropdown filter component
   */
  // Bootstrap multiselect dropdown
  const MultiSelectFilter = ({
    label,
    options,
    selected,
    onSelectedChange,
  }) => {
    // ===== LOCAL STATE =====
    const [isOpen, setIsOpen] = useState(false); // Dropdown visibility
    const dropdownRef = useRef(null); // Reference to dropdown DOM element

    /**
     * Close Dropdown on Outside Click
     * 
     * Adds/removes mousedown listener to document
     * Closes dropdown if click is outside the component
     * 
     * @effect
     * @dependency {boolean} isOpen - Re-attach listener when dropdown state changes
     */
    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isOpen]);

    return (
      <div className="dropdown" ref={dropdownRef}>
        {/* Dropdown toggle button */}
        <button
          className="btn btn-outline-primary btn-sm"
          type="button"
          onClick={() => setIsOpen(!isOpen)}
        >
          {label} {selected.length > 0 && <span className="badge bg-secondary ms-1">{selected.length}</span>}
          <span className="ms-1">▼</span>
        </button>

        {/* Dropdown menu with checkboxes */}
        <ul 
          className={`dropdown-menu ${isOpen ? 'show' : ''}`} 
          style={{ minWidth: "220px", maxHeight: "300px", overflowY: "auto" }}
          onClick={(e) => e.stopPropagation()}
        >
          {options.length === 0 ? (
            <li className="px-3 py-2 text-muted small">No options available</li>
          ) : (
            options.map((option) => {
              // Handle both object {id, label} and string formats
              const optionId = typeof option === 'object' ? option.id : option;
              const optionLabel = typeof option === 'object' ? option.label : option;
              
              return (
                <li key={optionId} className="px-2 py-1">
                  <div className="form-check d-flex align-items-center">
                    {/* Checkbox input */}
                    <input
                      className="form-check-input me-2"
                      type="checkbox"
                      id={`${label}-${optionId}`}
                      checked={selected.includes(optionId)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          // Add to selected
                          onSelectedChange([...selected, optionId]);
                        } else {
                          // Remove from selected
                          onSelectedChange(selected.filter((s) => s !== optionId));
                        }
                      }}
                      style={{ marginTop: '0', cursor: 'pointer' }}
                    />
                    {/* Label text */}
                    <label 
                      htmlFor={`${label}-${optionId}`} 
                      className="form-check-label mb-0" 
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      {optionLabel}
                    </label>
                  </div>
                </li>
              );
            })
          )}
        </ul>
      </div>
    );
  };

  /**
   * Main Component Render
   * 
   * Returns JSX for the agent routed exceptions management page
   * Layout:
   * 1. Page header with title and logo
   * 2. Info alert explaining exception filtering
   * 3. Loading spinner (if loading)
   * 4. Filter bar with status dropdown and search input
   * 5. Pagination controls
   * 6. Data table with sortable columns
   * 7. Empty state message (if no results)
   */
  return (
    <div className="main-content" style={{ paddingTop: 64 }}>
       <HeaderLogo />
      <div className="page-content">
        <div className="container-fluid">
          {/* PAGE HEADER */}
          <div id="table_height">
            <div className="d-flex align-items-center mb-2">
              {/* Icon badge */}
              <div className="bg-primary text-white rounded p-2 me-3">
                <CommonIcon iconName="ShieldAlert" size={"24px"}  color="#fff" />
              </div>
              {/* Page title and description */}
              <div className="flex-grow-1">
                <h1 className="h3 mb-0 fw-bold">Agent Routed Exceptions</h1>
                <p className="text-muted mb-0 small">Automated classification performance and continuous improvement metrics</p>
              </div>
              {/* Logo badge */}
     <div className="ms-auto bg-primary" style={{padding:"1px",border:"1px solid #000",position:"absolute", right:"5px",borderRadius:"2px",top:"15px"}}>
    <img src="../images/predatica_logo.png" alt="Logo" height={"20px"}  /> 
    </div>
            </div>  
    
            {/* Error message container */}
            <div id="app_err"></div>
          </div>

      {/* INFO ALERT */}
      <div className="alert alert-secondary small">
        Showing only contracts that the Agent could not classify with high confidence.
      </div>

      {/* LOADING SPINNER */}
      {loading && (
        <div className="text-center py-3">
          <div className="spinner-border text-primary spinner-border-sm me-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <span className="text-muted small">Loading contracts...</span>
        </div>
      )}

      {/* FILTER BAR */}
      <div className="card p-3 mb-4">
        <div className="d-flex flex-wrap gap-3 align-items-center">

          {/* MULTI-SELECT FILTERS (Currently Disabled) */}
          {/* <MultiSelectFilter
            label="Product Categories"
            options={productTaxonomies}
            selected={selectedProducts}
            onSelectedChange={setSelectedProducts}
          />

          <MultiSelectFilter
            label="Sales Channels"
            options={salesTaxonomies}
            selected={selectedChannels}
            onSelectedChange={setSelectedChannels}
          />

          <MultiSelectFilter
            label="Territories"
            options={territoryTaxonomies}
            selected={selectedTerritories}
            onSelectedChange={setSelectedTerritories}
          /> */}

          {/* STATUS FILTER DROPDOWN */}
          <select
            className="form-select form-select-sm"
            style={{ width: "200px" }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="EXCEPTIONS_PRESENT">Exceptions Present</option>
            <option value="NO_EXCEPTIONS">No Exceptions</option>
          </select>

          {/* SEARCH INPUT */}
          <div className="flex-grow-1" style={{ minWidth: "220px" }}>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search by contract name or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* PAGINATION CONTROLS */}
          <div className="d-flex align-items-center gap-3 ms-auto">
            {/* Results counter */}
            <div className="text-muted small">
              {filteredAndSortedContracts.length > 0 ? (
                <>
                  {((currentPage - 1) * rowsPerPage) + 1}–
                  {Math.min(currentPage * rowsPerPage, filteredAndSortedContracts.length)} of{" "}
                  {filteredAndSortedContracts.length}
                </>
              ) : (
                "0 of 0"
              )}
            </div>

            {/* Previous/Next buttons */}
            <div className="d-flex align-items-center">
              {/* Previous button */}
              <button
                className="btn btn-sm btn-light border-0"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1 || filteredAndSortedContracts.length === 0}
                style={{ opacity: (currentPage === 1 || filteredAndSortedContracts.length === 0) ? 0.4 : 1 }}
                title="Previous"
              >
                <ChevronLeft size={20} />
              </button>

              {/* Next button */}
              <button
                className="btn btn-sm btn-light border-0 ms-2"
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages || filteredAndSortedContracts.length === 0}
                style={{ opacity: (currentPage === totalPages || filteredAndSortedContracts.length === 0) ? 0.4 : 1 }}
                title="Next"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* DATA TABLE - SCROLLABLE CONTAINER */}
      <div 
        className="card" 
        style={{
          maxHeight: 'calc(100vh - 340px)',
          minHeight: '400px',
          overflowY: 'auto',
          overflowX: 'auto',
          position: 'relative',
          border: '1px solid #dee2e6',
          borderRadius: '8px',
          backgroundColor: '#fff',
        }}
      >
        <table className="table table-hover mb-0">
          {/* TABLE HEADER - STICKY POSITIONING */}
          <thead>
            <tr>
              {/* CONTRACT NAME COLUMN - SORTABLE */}
              <th 
                onClick={() => handleSort('name')}
                style={{
                  position: 'sticky',
                  top: 0,
                  backgroundColor: '#f8f9fa',
                  zIndex: 10,
                  fontSize: '11px',
                  fontWeight: '600',
                  padding: '12px 8px',
                  borderBottom: '2px solid #dee2e6',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                Contract <SortIcon columnKey="name" />
              </th>
              
              {/* EXCEPTION GROUPS COLUMN - NOT SORTABLE */}
              <th 
                style={{
                  position: 'sticky',
                  top: 0,
                  backgroundColor: '#f8f9fa',
                  zIndex: 10,
                  fontSize: '11px',
                  fontWeight: '600',
                  padding: '12px 8px',
                  borderBottom: '2px solid #dee2e6',
                }}
              >
                Groups impacted
              </th>
              
              {/* MINIMUM CONFIDENCE COLUMN - SORTABLE */}
              <th 
                onClick={() => handleSort('minConfidence')}
                style={{
                  position: 'sticky',
                  top: 0,
                  backgroundColor: '#f8f9fa',
                  zIndex: 10,
                  fontSize: '11px',
                  fontWeight: '600',
                  padding: '12px 8px',
                  borderBottom: '2px solid #dee2e6',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                Minimum confidence <SortIcon columnKey="minConfidence" />
              </th>
              
              {/* EXCEPTION COUNT COLUMN - SORTABLE */}
              <th 
                onClick={() => handleSort('totalExceptions')}
                style={{
                  position: 'sticky',
                  top: 0,
                  backgroundColor: '#f8f9fa',
                  zIndex: 10,
                  fontSize: '11px',
                  fontWeight: '600',
                  padding: '12px 8px',
                  borderBottom: '2px solid #dee2e6',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                Exceptions count <SortIcon columnKey="totalExceptions" />
              </th>
              
              {/* LAST ACTIVITY COLUMN - SORTABLE */}
              <th 
                onClick={() => handleSort('lastActivity')}
                style={{
                  position: 'sticky',
                  top: 0,
                  backgroundColor: '#f8f9fa',
                  zIndex: 10,
                  fontSize: '11px',
                  fontWeight: '600',
                  padding: '12px 8px',
                  borderBottom: '2px solid #dee2e6',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                Last activity <SortIcon columnKey="lastActivity" />
              </th>
            </tr>
          </thead>
          
          {/* TABLE BODY - DATA ROWS */}
          <tbody>
            {paginatedContracts.map((contract) => (
              <tr
                key={contract.id}
                style={{ cursor: "pointer" }}
                onClick={() => handleRowClick(contract)}
              >
                {/* CONTRACT NAME COLUMN */}
                <td
                  style={{
                    fontSize: '11px',
                    padding: '8px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                  <strong>{contract.name}</strong>
                  <br />
                  <span className="text-muted small">{contract.licensorName}</span>
                </td>

                {/* EXCEPTION GROUPS COLUMN - Shows badges for affected categories */}
                <td
                  style={{
                    fontSize: '11px',
                    padding: '8px',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                  {/* Product exceptions badge - shown if count > 0 */}
                  {contract.productExceptions > 0 && (
                    <span className="badge bg-secondary me-1" style={{ fontSize: '10px' }}>
                      Product ({contract.productExceptions})
                    </span>
                  )}
                  {/* Channel exceptions badge - shown if count > 0 */}
                  {contract.channelExceptions > 0 && (
                    <span className="badge bg-secondary me-1" style={{ fontSize: '10px' }}>
                      Channel ({contract.channelExceptions})
                    </span>
                  )}
                  {/* Territory exceptions badge - shown if count > 0 */}
                  {contract.territoryExceptions > 0 && (
                    <span className="badge bg-secondary" style={{ fontSize: '10px' }}>
                      Territory ({contract.territoryExceptions})
                    </span>
                  )}
                </td>

                {/* MINIMUM CONFIDENCE COLUMN - Color-coded by confidence level */}
                <td
                  style={{
                    fontSize: '11px',
                    padding: '8px',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                  {/* Red for very low confidence (< 40%) */}
                  {contract.minConfidence < 0.4 ? (
                    <span className="text-danger fw-semibold">
                      {(contract.minConfidence * 100).toFixed(2)}%
                    </span>
                  ) : contract.minConfidence < 0.6 ? (
                    /* Orange for medium confidence (40-60%) */
                    <span className="text-warning fw-semibold">
                      {(contract.minConfidence * 100).toFixed(2)}%
                    </span>
                  ) : (
                    /* Default color for higher confidence (> 60%) */
                    <span>{(contract.minConfidence * 100).toFixed(2)}%</span>
                  )}
                </td>

                {/* EXCEPTION COUNT COLUMN - Shows total exception count */}
                <td
                  style={{
                    fontSize: '11px',
                    padding: '8px',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                  <span className="badge bg-light text-dark border" style={{ fontSize: '10px' }}>
                    {contract.totalExceptions}
                  </span>
                </td>

                {/* LAST ACTIVITY COLUMN - Shows relative date (Today, Yesterday, N days ago) */}
                <td 
                  className="text-muted"
                  style={{
                    fontSize: '11px',
                    padding: '8px',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                  {formatDate(contract.lastActivity)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* EMPTY STATE - Shown when no results after filtering */}
        {paginatedContracts.length === 0 && !loading && (
          <div className="text-center py-5 text-muted">
            No contracts found matching your filters
          </div>
        )}
      </div>
   </div></div></div>
  );
}
